import {
  SOURCE_META_KEY,
  readCallSite,
  type LitSourceMeta,
} from '../source-meta.js';
import type {ElementInfo} from '../../types.js';

export interface ElementResolver {
  resolveElementInfo(el: Element): Promise<ElementInfo | null>;
}

/** Which elements the overlay can pick. */
export type HostTest = (el: Element) => boolean;

/** A component the source-meta transform stamped: it has a file to open. */
export const hasSourceMeta: HostTest = (el) =>
  (
    el.constructor as CustomElementConstructor & {
      [SOURCE_META_KEY]?: LitSourceMeta;
    }
  )[SOURCE_META_KEY] !== undefined;

/**
 * Any Lit element, stamped or not, for pages with no build-time transform
 * (`lit-devtools dev`). `elementProperties` is the static map every
 * `ReactiveElement` class carries; a plain custom element has none.
 */
const isLitElement: HostTest = (el) =>
  (el.constructor as {elementProperties?: unknown}).elementProperties instanceof
  Map;

/** What `hosts: 'lit'` picks: any Lit element, stamped or not. */
export const isAnyHost: HostTest = (el) =>
  hasSourceMeta(el) || isLitElement(el);

// Walk up from an element (crossing shadow boundaries) to the nearest host that
// passes `isHost` -- by default the one carrying Lit source metadata, i.e. the
// component that rendered it.
export const findSourceHost = (
  el: Element,
  isHost: HostTest = hasSourceMeta
): Element | null => {
  let current: Element | null = el;
  while (current !== null) {
    if (isHost(current)) {
      return current;
    }
    const root = current.getRootNode();
    if (root instanceof ShadowRoot) {
      current = root.host;
      continue;
    }
    current = current.parentElement;
  }
  return null;
};

// The next host out from a picked one, for stepping outward with the arrow
// keys. Unlike findSourceHost, which jumps from shadow content straight to the
// component that rendered it, this follows the composed tree, so a library
// element slotted into another (a checkbox in a card) steps to that one first.
export const findEnclosingHost = (
  el: Element,
  isHost: HostTest = hasSourceMeta
): Element | null => {
  let current: Element | null = el;
  while (current !== null) {
    const parent: Element | null = current.parentElement;
    if (parent !== null) {
      current = parent;
    } else {
      const root = current.getRootNode();
      current = root instanceof ShadowRoot ? root.host : null;
    }
    if (current !== null && isHost(current)) return current;
  }
  return null;
};

// Pierce nested shadow roots to find the deepest element under the cursor.
// document.elementFromPoint retargets shadow content to the top-level host, so
// we descend through each shadowRoot to reach the innermost element.
const deepElementFromPoint = (x: number, y: number): Element | null => {
  let el = document.elementFromPoint(x, y);
  let deepest: Element | null = el;
  while (el?.shadowRoot) {
    const inner = el.shadowRoot.elementFromPoint(x, y);
    if (inner === null || inner === el) break;
    deepest = inner;
    el = inner;
  }
  return deepest;
};

// Bounding-rect fallback for elements elementFromPoint misses — prefer the
// deepest matching host so nested components still beat their ancestors.
const hostByRect = (x: number, y: number, isHost: HostTest): Element | null => {
  let best: Element | null = null;
  for (const el of document.querySelectorAll('*')) {
    if (!isHost(el)) continue;
    const rect = el.getBoundingClientRect();
    const inside =
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    if (inside && (best === null || best.contains(el))) best = el;
  }
  return best;
};

// Resolve the source host under a viewport point. The optional dialog is the
// overlay's own modal, temporarily closed so it doesn't shadow the hit-test.
export const findSourceAtPoint = (
  x: number,
  y: number,
  dialog: HTMLDialogElement | null = null,
  isHost: HostTest = hasSourceMeta
): Element | null => {
  if (dialog !== null) dialog.close();
  try {
    const deepest = deepElementFromPoint(x, y);
    const host = deepest === null ? null : findSourceHost(deepest, isHost);
    return host ?? hostByRect(x, y, isHost);
  } finally {
    if (dialog !== null && !dialog.open) dialog.showModal();
  }
};

export const defaultResolver: ElementResolver = {
  async resolveElementInfo(el) {
    const host = findSourceHost(el);
    if (host === null) return null;
    const ctor = host.constructor as CustomElementConstructor & {
      [SOURCE_META_KEY]?: LitSourceMeta;
    };
    const meta = ctor[SOURCE_META_KEY];
    if (meta === undefined) return null;
    return {
      tagName: host.tagName.toLowerCase(),
      componentName: meta.componentName,
      source: {
        filePath: meta.filePath,
        lineNumber: meta.lineNumber,
      },
      callSite: readCallSite(host),
    };
  },
};

/**
 * What the tooltip shows for a host: its tag, and its file and line when the
 * source-meta transform stamped it. A library element carries no stamp, so it
 * is named after its class and has no source to open, but it may
 * still carry the call site of the template that wrote it.
 */
export const hostInfo = (
  host: Element
): Omit<ElementInfo, 'source'> & {source?: ElementInfo['source']} => {
  const meta = (
    host.constructor as CustomElementConstructor & {
      [SOURCE_META_KEY]?: LitSourceMeta;
    }
  )[SOURCE_META_KEY];
  const tagName = host.tagName.toLowerCase();
  const callSite = readCallSite(host);
  return meta === undefined
    ? {tagName, componentName: host.constructor.name || undefined, callSite}
    : {
        tagName,
        componentName: meta.componentName,
        source: {filePath: meta.filePath, lineNumber: meta.lineNumber},
        callSite,
      };
};
